<template #default>
    <div>
        <div class="mb-0">
            <h6 class="font-weight-bold text-info mb-3">
                <i class="mdi mdi-information-outline mr-1"></i>
                Basic Information
            </h6>
            <div class="row">
                <div class="col-md-3 mb-3">
                    <small class="text-muted d-block mb-1">Platform</small>
                    <b-badge
                        :variant="requestLog.pathPrefix == 'api' ? 'success' : requestLog.pathPrefix == 'app-api' ? 'primary' : 'secondary'"
                    >
                        {{ requestLog.pathPrefix == 'api' ? 'PORTAL' : requestLog.pathPrefix == 'app-api' ? 'APP' : 'N/A' }}
                    </b-badge>
                </div>
                <div class="col-md-3 mb-3">
                    <small class="text-muted d-block mb-1">Method</small>
                    <b-badge :variant="getMethodVariant(requestLog.method)">{{ requestLog.method || '--' }}</b-badge>
                </div>
                <div class="col-md-3 mb-3">
                    <small class="text-muted d-block mb-1">Success</small>
                    <b-badge :variant="requestLog.success ? 'success' : 'danger'">
                        {{ requestLog.success ? $t('label.yes') : $t('label.no') }}
                    </b-badge>
                </div>
                <div class="col-md-3 mb-3">
                    <small class="text-muted d-block mb-1">Status Code</small>
                    <div class="border rounded bg-light p-2">{{ requestLog.statusCode || '--' }}</div>
                </div>
                <div class="col-md-6 mb-3">
                    <small class="text-muted d-block mb-1">User</small>
                    <div class="border rounded bg-light p-2">{{ getUserName(requestLog.user) }}</div>
                </div>
                <div class="col-md-6 mb-3">
                    <small class="text-muted d-block mb-1">{{ $t('label.createdAt') }}</small>
                    <div class="border rounded bg-light p-2">{{ requestLog.createdAt || '--' }}</div>
                </div>
            </div>
        </div>
        <hr>
        <div class="mb-3">
            <h6 class="font-weight-bold text-info mb-3">
                <i class="mdi mdi-web mr-1"></i>
                Request
            </h6>
            <div class="row">
                <div class="col-md-12 mb-3">
                    <small class="text-muted d-block mb-1">URL</small>
                    <div class="border rounded bg-light p-2 text-primary">{{ requestLog.url || '--' }}</div>
                </div>
                <div class="col-md-12 mb-3">
                    <small class="text-muted d-block mb-1">Path</small>
                    <div class="border rounded bg-light p-2">{{ requestLog.path || '--' }}</div>
                </div>
                <div class="col-md-4 mb-3">
                    <small class="text-muted d-block mb-1">Scheme</small>
                    <div class="border rounded bg-light p-2">{{ requestLog.scheme || '--' }}</div>
                </div>
                <div class="col-md-4 mb-3">
                    <small class="text-muted d-block mb-1">Host</small>
                    <div class="border rounded bg-light p-2">{{ requestLog.host || '--' }}</div>
                </div>
                <div class="col-md-4 mb-3">
                    <small class="text-muted d-block mb-1">Port</small>
                    <div class="border rounded bg-light p-2">{{ requestLog.port || '--' }}</div>
                </div>
                <div class="col-md-12 mb-3">
                    <small class="text-muted d-block mb-1">User Agent</small>
                    <div class="border rounded bg-light p-2">{{ requestLog.userAgent || '--' }}</div>
                </div>
                <div class="col-md-12">
                    <small class="text-muted d-block mb-1">IP</small>
                    <div class="border rounded bg-light p-2">{{ requestLog.ip || '--' }}</div>
                </div>
            </div>
        </div>
        <hr>
        <div class="mb-3">
            <h6 class="font-weight-bold text-info mb-3">
                <i class="mdi mdi-speedometer mr-1"></i>
                Performance
            </h6>
            <div
                v-if="requestLog.performance"
                class="table-responsive"
            >
                <table class="table table-sm table-bordered mb-0">
                    <tbody>
                        <tr>
                            <td class="font-weight-bold text-muted text-nowrap">Memory Used</td>
                            <td>{{ requestLog.performance.memoryUsedMb }}</td>
                        </tr>
                        <tr>
                            <td class="font-weight-bold text-muted text-nowrap">Memory Peak</td>
                            <td>{{ requestLog.performance.memoryPeakMb }}</td>
                        </tr>
                        <tr>
                            <td class="font-weight-bold text-muted text-nowrap">Duration</td>
                            <td>{{ requestLog.performance.durationMs }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <span v-else>--</span>
        </div>
        <hr>
        <div class="mb-4">
            <h6 class="font-weight-bold text-info mb-3">
                <i class="mdi mdi-code-json mr-1"></i>
                Request Payload
            </h6>
            <div class="border rounded bg-light p-3 overflow-auto">
                <pre class="mb-0">{{ requestLog.requestPayload ? getJsonText(requestLog.requestPayload) : '--' }}</pre>
            </div>
        </div>
        <div class="mb-4">
            <h6 class="font-weight-bold text-info mb-3">
                <i class="mdi mdi-code-json mr-1"></i>
                Response Payload
            </h6>
            <div class="border rounded bg-light p-3 overflow-auto">
                <pre class="mb-0">{{ requestLog.responsePayload ? getJsonText(requestLog.responsePayload) : '--' }}</pre>
            </div>
        </div>
        <div class="mb-4">
            <h6 class="font-weight-bold text-info mb-3">
                <i class="mdi mdi-cookie mr-1"></i>
                Cookies
            </h6>
            <div class="border rounded bg-light p-3 overflow-auto">
                <pre class="mb-0">{{ requestLog.cookies ? getJsonText(requestLog.cookies) : '--' }}</pre>
            </div>
        </div>
        <div>
            <h6 class="font-weight-bold text-info mb-3">
                <i class="mdi mdi-file-multiple-outline mr-1"></i>
                Files
            </h6>
            <div class="border rounded bg-light p-3 overflow-auto">
                <pre class="mb-0">{{ requestLog.files ? getJsonText(requestLog.files) : '--' }}</pre>
            </div>
        </div>
        <hr class="my-3 mx-n4">
        <div class="d-flex justify-content-end">
            <b-button
                type="button"
                variant="danger"
                class="col-2"
                @click="$emit('done')"
            >
                {{ $t('label.cancel') }}
            </b-button>
        </div>
    </div>
</template>
<script>
export default {
    props: {
        requestLog: {
            type: Object,
            required: true,
        },
    },
    methods: {
        getJsonText(data) {
            if (typeof data == 'string') return data;
            return JSON.stringify(data, null, 2);
        },
        getUserName(user) {
            if(!user) return '--';
            return `${user.firstName} ${user.lastName}`;
        },
        getMethodVariant(method) {
            if(method == 'GET') return 'info';
            if(method == 'POST') return 'success';
            if(method == 'PUT') return 'warning';
            if(method == 'PATCH') return 'primary';
            if(method == 'DELETE') return 'danger';
            return 'secondary';
        },
    },
}
</script>
